import type { SentinelBreakdownRow } from './metric-keys';
import type { SentinelChannel, SentinelPerformanceRow, SentinelTurnBucket } from '@/types/sentinelAnalytics';
import type { AnalyticsDimension } from './analytics-page';
import { CHANNEL_LABEL, UNCATEGORIZED_CATEGORY } from './definitions';
import { hasBusinessTime, rowSnapshot, toPerformanceRow, type Snapshot } from './report';

const nameOf = ({ dims }: SentinelBreakdownRow, dimension: AnalyticsDimension): string =>
  dimension === 'channel' ? (CHANNEL_LABEL[dims.channel as SentinelChannel] ?? dims.channel ?? 'Other') : (dims.category ?? UNCATEGORIZED_CATEGORY);

/** A sub-category row belongs to its category; tickets with no sub-category stay in the category's own row. */
const subRowsOf = (category: SentinelBreakdownRow, subGroups: SentinelBreakdownRow[]) =>
  subGroups.filter(({ dims }) => dims.subcategory && dims.category === category.dims.category);

/** A Ticket or Message Metrics table's rows: one per `dimension` value, a category's sub-categories under it. */
export function toTableRows<T>(groups: SentinelBreakdownRow[], dimension: AnalyticsDimension, subGroups: SentinelBreakdownRow[], toMetrics: (get: Snapshot) => T) {
  return groups.map((group) => ({
    name: nameOf(group, dimension),
    ...toMetrics(rowSnapshot(group)),
    subRows: subRowsOf(group, subGroups).map((sub) => ({ name: sub.dims.subcategory!, ...toMetrics(rowSnapshot(sub)) })),
  }));
}

type PerformanceRow = SentinelPerformanceRow & { name: string };

const performanceRow = (group: SentinelBreakdownRow, name: string): PerformanceRow => ({
  ...toPerformanceRow(group.dims.bucket as SentinelTurnBucket, rowSnapshot(group)),
  name,
});

/**
 * The Performance table's rows from `[dimension, bucket]` groups, and each category's sub-category rows from
 * `[category, subcategory, bucket]` groups. Tickets Sentinel never handled have no bucket and no row.
 */
export function toPerformanceTable(groups: SentinelBreakdownRow[], dimension: AnalyticsDimension, subGroups: SentinelBreakdownRow[]) {
  const subRows: Record<string, PerformanceRow[]> = Object.create(null);
  for (const sub of subGroups) {
    if (sub.dims.bucket && sub.dims.subcategory) (subRows[nameOf(sub, 'category')] ??= []).push(performanceRow(sub, sub.dims.subcategory));
  }
  return {
    rows: groups.filter(({ dims }) => dims.bucket).map((group) => performanceRow(group, nameOf(group, dimension))),
    subRows,
    businessAvailable: groups.some((group) => hasBusinessTime(rowSnapshot(group))),
  };
}
